import React, { forwardRef, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

/** 고정 BottomBar 높이 + safe area를 측정해 스크롤 영역 pb에 사용 */
export function useBottomBarReserve(active = true) {
  const bottomBarRef = useRef(null);
  const { navBar } = useSelector((state) => state.systemBar);
  const fallback = useMemo(
    () => `calc(${navBar}px + 148px)`,
    [navBar]
  );
  const [bottomReserve, setBottomReserve] = useState(active ? fallback : '24px');

  useLayoutEffect(() => {
    if (!active) {
      setBottomReserve('24px');
      return;
    }

    const el = bottomBarRef.current;
    if (!el) {
      setBottomReserve(fallback);
      return;
    }

    const update = () => {
      setBottomReserve(`${Math.ceil(el.getBoundingClientRect().height) + 8}px`);
    };

    update();

    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
    };
  }, [active, navBar, fallback]);

  return { bottomBarRef, bottomReserve };
}

// [버튼] 하단 고정 영역 공통 컨테이너
// variant:
//   'default'  — 흰색 배경 + 상단 보더  (M_PER_NEW_01 스타일)
//   'gradient' — 위로 사라지는 그라데이션 (M_PER_NEW_02 스타일)
//   'blur'     — frosted-glass 효과      (M_PER_NEW_03 스타일)
// navBar 패딩은 내부에서 자동 적용됨 (systemBar Redux)
export const BottomBar = forwardRef(function BottomBar(
  { children, variant = 'default', style },
  ref
) {
  const { navBar } = useSelector((state) => state.systemBar);

  const cls = {
    default:
      'fixed right-0 bottom-0 left-0 z-40 border-t border-slate-100 bg-white/95 px-4 pt-3 backdrop-blur-md',
    gradient:
      'fixed right-0 bottom-0 left-0 z-50 bg-gradient-to-t from-[#f8f9fb] via-[#f8f9fb]/95 to-transparent p-6',
    blur: 'fixed bottom-0 left-0 w-full bg-white/90 px-6 py-2 shadow-[0_-10px_30px_rgba(0,0,0,0.04)] backdrop-blur-xl',
  };
  return (
    <div
      ref={ref}
      className={cls[variant]}
      style={{
        paddingBottom: `calc(1rem + max(${navBar}px, env(safe-area-inset-bottom, 0px)))`,
        ...style,
      }}
    >
      {children}
    </div>
  );
});

// [버튼] 버튼 행 래퍼 — 가로 배치 (flex gap-3)
export function ButtonRow({ children }) {
  return <div className="flex gap-3">{children}</div>;
}

// [버튼] Primary 버튼 — 초록 (승인 / 신청 / 수정 등)
// size : 'md'(h-12, 기본) | 'lg'(h-14)
// flex : Tailwind flex 클래스 (예: 'flex-1', 'flex-2', 'flex-[2]', 'w-full')
export function PrimaryButton({
  children,
  onClick,
  disabled = false,
  size = 'md',
  flex = 'flex-1',
  icon,
  className = '',
}) {
  const sizeClass = size === 'lg' ? 'h-14 text-lg' : 'h-12 text-sm';
  const stateClass = disabled
    ? 'cursor-not-allowed bg-[#5ABD4F]/40 opacity-70 grayscale-[0.5]'
    : 'bg-[#5ABD4F] shadow-lg shadow-[#5ABD4F]/20';

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`font-headline flex items-center justify-center gap-2 rounded-full font-bold text-white transition-all active:scale-[0.98] ${sizeClass} ${flex} ${stateClass} ${className}`.trim()}
    >
      {icon}
      {children}
    </button>
  );
}

// [버튼] Danger 버튼 — 빨간 테두리 (반려 / 삭제 등)
export function DangerButton({ children, onClick, flex = 'flex-1', className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-headline h-12 rounded-full border border-[#ba1a1a]/30 bg-white text-sm font-bold text-[#ba1a1a] active:bg-red-50 ${flex} ${className}`.trim()}
    >
      {children}
    </button>
  );
}

// [버튼] Secondary 버튼 — 회색 (이전 / 임시저장 등)
export function SecondaryButton({ children, onClick, flex = 'flex-1', className = '' }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`font-headline h-12 rounded-full border border-[#becab7]/20 bg-[#e7e8ea] text-sm font-extrabold text-[#3f4a3b] transition-all active:scale-[0.98] ${flex} ${className}`.trim()}
    >
      {children}
    </button>
  );
}
